import { useEffect, useState } from 'react';

import { isJsonUrl } from '@/utils';
import {
  SEARCH_ACTION_TYPES,
  type ISearchAction,
  type ISearchResultApiResponse,
  type ISearchState,
} from './ComposableContentBlock.interface';
import { dataLayerPush } from '@/utils/gtm';

export function useFetchResult(
  apiUrl: string,
  queryString: string,
  page: number,
) {
  const [status, setStatus] = useState<string>('idle');
  const [result, setResult] = useState<ISearchResultApiResponse>({
    items: [],
    hasNextPage: false,
  });

  const isLocal =
    process.env.NODE_ENV === 'development'
      ? 'https://dev.missionaustralia.com.au'
      : '';

  useEffect(() => {
    const fetchData = async () => {
      if (!queryString || !apiUrl) {
        // Added apiUrl check
        setStatus('idle');
        return;
      }
      try {
        setStatus('loading');

        const response = await fetch(
          isJsonUrl(apiUrl)
            ? `${apiUrl}`
            : `${isLocal}${apiUrl}&${queryString}`,
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const data: ISearchResultApiResponse = await response.json();
        dataLayerPush(data.gtmDataLayerObject);

        if (data.items?.length > 0) {
          setStatus('success');

          if (page === 1) {
            setResult(data);
          } else {
            // Important: Use functional update for appending results based on previous state
            setResult((prevData: any) => ({
              ...data,
              items: [...(prevData?.items || []), ...data.items],
            }));
          }
        } else {
          // Different handling based on page number
          if (page === 1) {
            setStatus('no-result');
            setResult({ items: [], hasNextPage: false });
          } else {
            // For pagination with no more content - keep existing items
            setStatus('no-more-content');
            setResult((prev: any) => ({
              ...prev,
              hasNextPage: false,
            }));
          }
        }
      } catch (error) {
        setStatus('error');
        setResult({ items: [], hasNextPage: false });
      }
    };
    fetchData();
  }, [apiUrl, queryString, page]); // Add 'page' back as dependency

  return {
    status,
    result,
  };
}

export function searchReducer(
  state: ISearchState,
  action: ISearchAction,
): ISearchState {
  switch (action.type) {
    case SEARCH_ACTION_TYPES.SET_OPEN_DROPDOWN:
      return { ...state, openDropdown: action.payload };
    case SEARCH_ACTION_TYPES.SET_OPEN_TAG_DROPDOWN:
      return { ...state, openTagDropdown: action.payload };
    case SEARCH_ACTION_TYPES.SET_PAGE:
      return { ...state, page: action.payload };
    case SEARCH_ACTION_TYPES.NEXT_PAGE:
      return { ...state, page: state.page + 1 };
    case SEARCH_ACTION_TYPES.SET_CATEGORY:
      return {
        ...state,
        selectedCategoryIndex: action.payload,
        page: 1, // Reset page when category changes
      };
    case SEARCH_ACTION_TYPES.SET_TAG_CATEGORY:
      return {
        ...state,
        selectedTagCategoryIndex: action.payload,
        page: 1, // Reset page when tag category changes
      };
    case SEARCH_ACTION_TYPES.SET_SEARCH_KEY:
      return { ...state, searchKey: action.payload };
    case SEARCH_ACTION_TYPES.SET_DEBOUNCED_SEARCH_KEY:
      return {
        ...state,
        debouncedSearchKey: action.payload,
        page: 1, // Reset page when search changes
      };
    default:
      return state;
  }
}
